import React from "react";
import { Link } from "react-router-dom";

import { Collapsible, useScrollProgress } from "@/components/collapsible";
import { Stack, Text } from "@/components/ui";
import { cn } from "@/utils/cn";

import {
  HomeAwayLabel,
  LiveIndicator,
  TeamScore,
  TeamAvatar,
  LeagueBadge,
} from "./primitives";

import type { Sport, Team } from "@/schemas";
import type { SportGameDetails } from "@/schemas/sport-game";

const TeamAvatarLink = ({
  team,
  sportType,
}: {
  team: Team;
  sportType: string;
}) => {
  if (team.team_id == null) {
    return <TeamAvatar team={team} size="lg" />;
  }
  return (
    <Link
      to={`/sports/${sportType}/teams/${team.team_id}`}
      aria-label={team.name}
      onClick={(e) => e.stopPropagation()}
    >
      <TeamAvatar team={team} size="lg" />
    </Link>
  );
};

const TeamColumn = ({
  team,
  side,
  renderAvatar,
}: {
  team: Team;
  side: "home" | "away";
  renderAvatar: (team: Team) => React.ReactNode;
}) => (
  <Stack alignItems="center" className="min-w-0" space={2}>
    {renderAvatar(team)}
    <Stack className="w-full min-w-0">
      <Text size="lg" weight="medium" align="center" truncate>
        {team.name}
      </Text>
      <HomeAwayLabel side={side} size="sm" align="center" />
    </Stack>
  </Stack>
);

interface PrimaryScorecardProps {
  event: Sport | SportGameDetails;
  className?: string;
  linkTeams?: boolean;
}

const PrimaryScorecard = ({
  event,
  className,
  linkTeams = false,
}: PrimaryScorecardProps) => {
  const progress = useScrollProgress(120);

  const renderAvatar = (team: Team) =>
    linkTeams ? (
      <TeamAvatarLink team={team} sportType={event.sport_type} />
    ) : (
      <TeamAvatar team={team} size="lg" />
    );

  const boxScore = "box_score" in event ? event.box_score : null;

  return (
    <div
      className={cn(
        "rounded-[28px] border bg-white px-6 py-5 dark:bg-gray-900",
        className,
      )}
    >
      <Collapsible progress={progress}>
        <Stack space={2}>
          <div className="flex items-center gap-3">
            <div className="h-px grow bg-border" />
            <LeagueBadge league={event.league} />
            <div className="h-px grow bg-border" />
          </div>
          {event.data.is_live && (
            <LiveIndicator
              data={event.data}
              size="full"
              textSize="md"
              iconSpacing={2}
            />
          )}
        </Stack>
      </Collapsible>

      <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-4">
        <TeamColumn
          team={event.data.home_team}
          side="home"
          renderAvatar={renderAvatar}
        />

        <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <TeamScore value={event.data.home_team.score} align="right" />
          <Text size="md" weight="bold">
            -
          </Text>
          <TeamScore value={event.data.away_team.score} align="left" />
        </div>

        <TeamColumn
          team={event.data.away_team}
          side="away"
          renderAvatar={renderAvatar}
        />
      </div>

      {boxScore && (
        <Collapsible progress={progress} marginTop={16}>
          <div className="rounded-2xl border px-4 py-3">
            <div
              className="grid gap-x-2 gap-y-2 text-center"
              style={{
                gridTemplateColumns: `max-content repeat(${Object.keys(boxScore.columns).length},minmax(0,1fr))`,
              }}
            >
              <div />
              {boxScore.columns.map((column) => (
                <Text key={column} size="xs" theme="muted">
                  {column}
                </Text>
              ))}

              <Text size="md" weight="medium">
                {boxScore.home.abbreviation}
              </Text>
              {boxScore.home.scores.map((score, index) => (
                <Text key={`${boxScore.home.abbreviation}-${index}`} size="md">
                  {score || "-"}
                </Text>
              ))}

              <Text size="md" weight="medium">
                {boxScore.away.abbreviation}
              </Text>
              {boxScore.away.scores.map((score, index) => (
                <Text key={`${boxScore.away.abbreviation}-${index}`} size="md">
                  {score || "-"}
                </Text>
              ))}
            </div>
          </div>
        </Collapsible>
      )}
    </div>
  );
};

export default PrimaryScorecard;
